"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Download, X } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { usePwa } from "./PwaProvider";

export function InstallPrompt() {
  const { ios, mobile, standalone } = usePwa();
  const pathname = usePathname();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [open, setOpen] = useState(false);

  // Only show on the root landing page
  const isLandingPage = pathname === "/";

  useEffect(() => {
    if (typeof window === "undefined" || standalone || !isLandingPage) return;
    
    // Automatically show for Desktop/PC users after 2 seconds
    if (!mobile && !ios) {
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [standalone, mobile, ios, isLandingPage]);

  useEffect(() => {
    if (typeof window === "undefined" || standalone || !isLandingPage) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setOpen(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [standalone, isLandingPage]);

  useEffect(() => {
    // Show iOS hint if not standalone and on landing page
    if (ios && !standalone && isLandingPage) {
      setOpen(true);
    }
  }, [ios, standalone, isLandingPage]);

  // Hide component if dismiss is triggered or we navigate away from landing page
  useEffect(() => {
    if (!isLandingPage) {
      setOpen(false);
    }
  }, [isLandingPage]);

  const handleDismiss = () => {
    setOpen(false);
  };

  if (standalone) return null;
  if (!open) return null;
  if (!isLandingPage) return null;

  const renderInstructions = () => {
    if (ios) {
      return (
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground text-center sm:text-left">
          On iPhone or iPad: tap <span className="font-semibold text-foreground">Share</span> →{" "}
          <span className="font-semibold text-foreground">Add to Home Screen</span>.
        </p>
      );
    }

    if (mobile) {
      return (
        <div className="space-y-1 mt-1 text-center sm:text-left">
          <p className="text-xs leading-relaxed text-muted-foreground">
            Install for quicker access and improved offline support—ideal for frontline teams.
          </p>
          <p className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest">
            Android Instructions: Tap <span className="text-foreground">Settings (⋮)</span> → <span className="text-foreground">Install App</span>
          </p>
        </div>
      );
    }

    // Explicit Desktop instructions
    return (
      <div className="space-y-1 mt-1 text-center sm:text-left">
        <p className="text-xs leading-relaxed text-muted-foreground">
          Install the attendance app on your computer for a professional fullscreen experience.
        </p>
        <p className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest">
          PC/Desktop: Click the <span className="text-foreground">Install (⊕)</span> button in your address bar.
        </p>
      </div>
    );
  };

  return (
    <div className="fixed inset-x-0 bottom-[max(1.5rem,env(safe-area-inset-bottom))] z-50 px-4 animate-in fade-in slide-in-from-bottom-5 duration-700">
      <div className="mx-auto max-w-lg">
        <Card className="border-border/80 p-5 shadow-2xl shadow-black/20 dark:shadow-black/50 overflow-hidden relative">
          {/* Subtle top glow for premium look */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
          
          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-5">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-border bg-muted/30 shadow-inner">
                <Download className="h-6 w-6 text-primary" aria-hidden />
              </span>
              <div className="flex flex-col">
                <div className="text-base font-bold tracking-tight text-center sm:text-left">Install Attendance App</div>
                {renderInstructions()}
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="absolute top-2 right-2 h-8 w-8 p-0 rounded-full hover:bg-muted" 
              onClick={handleDismiss} 
              aria-label="Close"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>

          {!ios && (
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                className="w-full sm:w-auto px-8"
                onClick={async () => {
                  if (!deferredPrompt) {
                    alert("Please use the (+) Install button in your browser's address bar.");
                    return;
                  }
                  deferredPrompt.prompt();
                  await deferredPrompt.userChoice;
                  setDeferredPrompt(null);
                  setOpen(false);
                }}
              >
                Install Now
              </Button>
              <Button variant="outline" className="w-full sm:w-auto" onClick={handleDismiss}>
                Maybe Later
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

